/**
 * Product Reviews Modal
 *
 * Fetches Yotpo Storefront v3 reviews + bottomline for the current product,
 * renders a custom summary + infinite-scroll review list inside a slide-up
 * modal.
 *
 * Endpoints (https://apidocs.yotpo.com/v3.0/reference/storefront-product-reviews):
 *   GET https://api-cdn.yotpo.com/v3/storefront/store/{appKey}/product/{productId}/reviews?per_page=N&page=N
 *   GET https://api-cdn.yotpo.com/v3/storefront/store/{appKey}/product/{productId}/bottomline
 *
 * Auth: X-Yotpo-Token header set to the public app key (same value as the
 * store id segment of the URL).
 */

(() => {
  if (customElements.get('product-reviews-modal')) return;

  const API_HOST = 'https://api-cdn.yotpo.com';
  // Yotpo returns a different bottomline.totalReview per page size (its
  // per-perPage aggregate snapshots have drifted). perPage=1 and perPage=10
  // return stale counts; perPage=20 returns the accurate total. This value
  // MUST stay in sync with REVIEWS_PER_PAGE in
  // assets/product-reviews-summary.js so the modal header and the buy-bar
  // summary always read the same count. Drives prewarm, list pagination, and
  // the header count (read from the first list page's bottomline).
  const PER_PAGE = 20;
  // Batches auto-loaded when the modal opens (2 × PER_PAGE = 40 reviews) so the
  // initial view feels substantial. After that, further reviews load only via
  // the user-initiated "Load more reviews" button — no silent scroll loading.
  const AUTO_LOAD_BATCHES = 2;
  const PREWARM_TIMEOUT_MS = 3000;
  const PREWARM_FALLBACK_MS = 1500;

  const HTML_ESCAPE = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  const escapeHTML = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (m) => HTML_ESCAPE[m]);

  const dotsHTML = (score, size) => {
    const out = [];
    for (let i = 1; i <= 5; i += 1) {
      let pct = 0;
      if (score >= i) pct = 100;
      else if (score > i - 1) pct = Math.round((score - (i - 1)) * 100);
      out.push(`<span class="product-reviews-modal__dot" style="--fill:${pct}%"></span>`);
    }
    const sizeClass = size === 'small' ? ' product-reviews-modal__dots--small' : '';
    return `<div class="product-reviews-modal__dots${sizeClass}" aria-hidden="true">${out.join('')}</div>`;
  };

  const formatDate = (iso, locale) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        month: 'long',
        year: 'numeric',
      }).format(d);
    } catch (_) {
      return d.toDateString();
    }
  };

  const reviewerName = (review, fallback) =>
    review?.user?.displayName ||
    review?.user?.display_name ||
    review?.name ||
    review?.reviewer?.name ||
    fallback;

  const reviewCreatedAt = (review) => review?.createdAt || review?.created_at || '';

  const pickBottomline = (data) => data?.bottomline || data?.response?.bottomline || data || {};

  const bottomlineScore = (bl) =>
    parseFloat(bl?.averageScore ?? bl?.average_score ?? bl?.score ?? 0) || 0;

  const bottomlineTotal = (bl) =>
    parseInt(bl?.totalReview ?? bl?.total_reviews ?? bl?.total_review ?? 0, 10) || 0;

  class ProductReviewsModal extends ModalWindow {
    constructor() {
      super();
      this.loaded = false;
      this.loading = false;
      this.done = false;
      this.page = 0;
      this.shown = 0;
      // Canonical bottomline captured from page 1 — the single source of truth
      // for both the header total and the counter's {total}, so they can never
      // disagree within one view (matches the buy-bar summary's number).
      this.canonicalTotal = 0;
      this.canonicalScore = 0;
      this.controller = null;
      this.requestId = 0;
      this.loadMoreButton = null;
      this.boundHandleLoadMore = this.handleLoadMore.bind(this);
      this.boundProductChange = null;

      this.appKey = this.dataset.yotpoAppKey;
      this.productId = this.dataset.productId;
      this.locale = this.dataset.pageLocale || document.documentElement.lang || 'en';

      this.labels = {
        ratings: this.dataset.lRatings || 'Ratings',
        loading: this.dataset.lLoading || 'Loading…',
        empty: this.dataset.lEmpty || 'No reviews yet.',
        error: this.dataset.lError || 'Unable to load reviews. Please try again.',
        anonymous: this.dataset.lAnonymous || 'Anonymous',
        counter: this.dataset.lCounter || 'Showing {shown} of {total} reviews',
        endState: this.dataset.lEndState || "You've reached the end of the reviews.",
        loadMore: this.dataset.lLoadMore || 'Load more reviews',
      };

      this.refs = {
        summary: this.querySelector('[data-reviews-summary]'),
        list: this.querySelector('[data-reviews-list]'),
        counter: this.querySelector('[data-reviews-counter]'),
        status: this.querySelector('[data-reviews-status]'),
        endState: this.querySelector('[data-reviews-end-state]'),
        loadMore: this.querySelector('[data-reviews-load-more]'),
      };
    }

    show(opener) {
      this.opener = opener;
      this.style.display = 'block';
      this.classList.add('modal-show');

      if (typeof lazyLoadInstance !== 'undefined' && lazyLoadInstance) {
        lazyLoadInstance.update();
      }

      setTimeout(() => {
        document.body.style.overflow = 'hidden';
        this.classList.add('modal-visuallyshow');
      }, 1);

      requestAnimationFrame(() => {
        if (typeof trapFocus === 'function') trapFocus(this);
      });

      if (!this.loaded) {
        this.loaded = true;
        this.bootstrap();
      }
    }

    hide() {
      super.hide();
      this.controller?.abort();
      // Invalidate any awaited response (including a prewarm awaited mid-close).
      this.requestId += 1;
    }

    connectedCallback() {
      super.connectedCallback();
      this.schedulePrewarm();

      // Sibling-product swap inside a Combined Listing dispatches a bubbling
      // `product:change-requested` event from _DefaultProductOptionSelects.
      // Rebind our reviews fetch to the new productId.
      this.boundProductChange = this.handleProductChange.bind(this);
      document.addEventListener('product:change-requested', this.boundProductChange);
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
      if (this.boundProductChange) {
        document.removeEventListener('product:change-requested', this.boundProductChange);
        this.boundProductChange = null;
      }
    }

    schedulePrewarm() {
      if (this.prewarmScheduled) return;
      if (!this.appKey || !this.productId) return;
      this.prewarmScheduled = true;

      const run = () => this.prewarm();
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: PREWARM_TIMEOUT_MS });
      } else {
        setTimeout(run, PREWARM_FALLBACK_MS);
      }
    }

    prewarm() {
      if (this.prewarmPromise || this.loaded) return;
      this.prewarmPromise = this.fetchYotpo(`reviews?perPage=${PER_PAGE}&page=1`).catch((err) => {
        if (err?.name !== 'AbortError') {
          console.warn('[product-reviews-modal] prewarm failed', err);
        }
        return null;
      });
    }

    async bootstrap() {
      if (!this.appKey || !this.productId) {
        this.setStatus(this.labels.error);
        return;
      }
      // Auto-load the opening batches, then hand control to the "Load more"
      // button. No scroll listener — loading past the initial view is always
      // user-initiated.
      for (let batch = 0; batch < AUTO_LOAD_BATCHES && !this.done; batch += 1) {
        const loaded = await this.fetchNextPage();
        // Stop auto-loading on a stale/error/empty result, but fall through to
        // the post-loop guard: a transient error (this.done still false) then
        // still surfaces the "Load more" button so the shopper can retry. The
        // genuine empty-first-page case sets this.done = true, which the guard
        // below correctly treats as "no button".
        if (!loaded) break;
      }
      if (!this.done) this.showLoadMoreButton();
    }

    showLoadMoreButton() {
      if (this.done || !this.refs.loadMore) return;
      if (!this.loadMoreButton) {
        this.loadMoreButton = document.createElement('button');
        this.loadMoreButton.type = 'button';
        this.loadMoreButton.className = 'product-reviews-modal__load-more-button';
        this.loadMoreButton.textContent = this.labels.loadMore;
        this.loadMoreButton.addEventListener('click', this.boundHandleLoadMore);
        this.refs.loadMore.appendChild(this.loadMoreButton);
        // The button is appended after show()'s trapFocus already snapshotted
        // the modal's focusable set, so it would otherwise be unreachable by
        // Tab. Re-snapshot the trap now that the button exists.
        this.refreshFocusTrap();
      }
      this.loadMoreButton.disabled = false;
      this.refs.loadMore.hidden = false;
    }

    removeLoadMoreButton() {
      const hadButton = this.loadMoreButton !== null;
      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener('click', this.boundHandleLoadMore);
        this.loadMoreButton.remove();
        this.loadMoreButton = null;
      }
      if (this.refs.loadMore) this.refs.loadMore.hidden = true;
      // Drop the removed button from the focus trap's snapshot so Tab no longer
      // targets a detached element.
      if (hadButton) this.refreshFocusTrap();
    }

    /**
     * Re-snapshots the modal's focus trap after its focusable set changes.
     * trapFocus() (assets/global.js) calls removeTrapFocus() before binding, and
     * the handlers live on a single shared object, so re-calling swaps the one
     * handler set rather than stacking listeners. Guarded on the modal being
     * open so we never trap focus on a hidden modal; trapFocus's default
     * elementToFocus is the container (this), which is not focusable, so this
     * refresh does not move the user's current focus.
     */
    refreshFocusTrap() {
      if (typeof trapFocus === 'function' && this.classList.contains('modal-show')) {
        trapFocus(this);
      }
    }

    async handleLoadMore() {
      if (this.loading || this.done) return;
      if (this.loadMoreButton) this.loadMoreButton.disabled = true;

      // Index of the first card this batch will append (headings align 1:1 with
      // review cards, so this is where keyboard/SR focus should land).
      const firstNewReviewIndex = this.shown;
      const loaded = await this.fetchNextPage();

      if (this.done) {
        // Last batch just loaded — remove the button and move focus to the
        // end-state so focus never lands on the removed button.
        this.removeLoadMoreButton();
        this.focusEndState();
        return;
      }

      if (loaded) this.focusFirstNewReview(firstNewReviewIndex);

      // More reviews remain (or the fetch failed / was stale) — keep the button
      // available so the shopper can continue or retry.
      if (this.loadMoreButton) {
        this.loadMoreButton.disabled = false;
        this.refs.loadMore.hidden = false;
        // On a non-advancing result (error/stale) focus was pulled to the close
        // button when the button was disabled; return it to the button so the
        // shopper can retry from where they were.
        if (!loaded) this.loadMoreButton.focus();
      }
    }

    focusFirstNewReview(startIndex) {
      const headings = this.refs.list.querySelectorAll(
        '.product-reviews-modal__review-title'
      );
      const heading = headings[startIndex];
      if (heading) heading.focus();
    }

    focusEndState() {
      if (this.refs.endState && !this.refs.endState.hidden) {
        this.refs.endState.setAttribute('tabindex', '-1');
        this.refs.endState.focus();
      }
    }

    handleProductChange(event) {
      const newId = String(event?.detail?.productId || '');
      if (!newId || newId === String(this.productId)) return;
      this.resetForProduct(newId);
    }

    resetForProduct(newId) {
      this.controller?.abort();
      this.requestId += 1;
      this.prewarmPromise = null;
      this.prewarmScheduled = false;
      this.loaded = false;
      this.loading = false;
      this.done = false;
      this.page = 0;
      this.shown = 0;
      this.canonicalTotal = 0;
      this.canonicalScore = 0;

      this.removeLoadMoreButton();

      if (this.refs.summary) this.refs.summary.innerHTML = '';
      if (this.refs.list) this.refs.list.innerHTML = '';
      if (this.refs.counter) {
        this.refs.counter.textContent = '';
        this.refs.counter.hidden = true;
      }
      if (this.refs.endState) {
        this.refs.endState.textContent = '';
        this.refs.endState.hidden = true;
        this.refs.endState.removeAttribute('tabindex');
      }
      this.setStatus('');

      this.productId = newId;
      this.dataset.productId = newId;

      if (this.classList.contains('modal-show')) {
        // Modal is open — fetch fresh reviews for the new product now.
        this.loaded = true;
        this.bootstrap();
      } else {
        // Modal closed — re-arm idle prewarm so the next open is instant.
        this.schedulePrewarm();
      }
    }

    async fetchYotpo(path, signal) {
      const url = `${API_HOST}/v3/storefront/store/${encodeURIComponent(this.appKey)}/product/${encodeURIComponent(this.productId)}/${path}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Yotpo-Token': this.appKey,
        },
        signal,
      });
      if (!res.ok) throw new Error(`Yotpo ${path} → HTTP ${res.status}`);
      return res.json();
    }

    /**
     * Loads and appends the next page of reviews.
     * @returns {Promise<boolean>} true if a batch of reviews was appended.
     */
    async fetchNextPage() {
      if (this.loading || this.done) return false;
      this.loading = true;
      this.setStatus(this.labels.loading);

      const next = this.page + 1;
      const requestId = ++this.requestId;
      this.controller = new AbortController();

      try {
        let raw = null;
        if (next === 1 && this.prewarmPromise) {
          const prewarmed = this.prewarmPromise;
          this.prewarmPromise = null;
          raw = await prewarmed;
        }
        if (!raw) {
          raw = await this.fetchYotpo(
            `reviews?perPage=${PER_PAGE}&page=${next}`,
            this.controller.signal
          );
        }

        // Drop stale response — modal closed/reopened or a later page beat us.
        if (requestId !== this.requestId) return false;

        // v3 storefront is flat; v1 widget wraps in `response` — handle both.
        const data = raw?.reviews ? raw : raw?.response || raw || {};
        const reviews = data.reviews || [];
        const pagination = data.pagination || {};
        this.page = pagination.page || next;

        // Capture the canonical bottomline once, from page 1. It drives BOTH
        // the header total and the counter's {total} so they can never disagree
        // — never sourced from pagination.total (which skews per page size).
        if (next === 1) {
          const bottomline = pickBottomline(data);
          this.canonicalTotal = bottomlineTotal(bottomline);
          this.canonicalScore = bottomlineScore(bottomline);
        }

        if (next === 1 && reviews.length === 0) {
          this.setStatus(this.labels.empty);
          this.done = true;
          return false;
        }

        if (next === 1 && !this.refs.summary.childElementCount) {
          this.renderSummary();
        }

        this.appendReviews(reviews);
        this.updateCounter();
        this.setStatus('');

        // Terminate on a short/empty page or once every review is shown, not on
        // pagination.total — robust to Yotpo per-page-size snapshot skew and
        // guarantees the list reaches the real end without contradicting the
        // header count.
        const reachedCanonicalTotal =
          this.canonicalTotal > 0 && this.shown >= this.canonicalTotal;
        if (reviews.length < PER_PAGE || reachedCanonicalTotal) {
          this.showEndState();
          this.done = true;
        }
        return true;
      } catch (err) {
        if (err?.name === 'AbortError') return false;
        console.error('[product-reviews-modal] reviews fetch failed', err);
        this.setStatus(this.labels.error);
        return false;
      } finally {
        this.loading = false;
      }
    }

    renderSummary() {
      const score = this.canonicalScore;
      const total = this.canonicalTotal;
      if (!total && !score) return;

      const formattedTotal = total.toLocaleString(this.locale);
      const formattedScore = score.toFixed(1);

      this.refs.summary.innerHTML = `
        <div class="product-reviews-modal__score">${escapeHTML(formattedScore)}</div>
        <div class="product-reviews-modal__summary-meta">
          ${dotsHTML(score)}
          <div class="product-reviews-modal__total">${escapeHTML(`${formattedTotal} ${this.labels.ratings}`)}</div>
        </div>
      `;
    }

    appendReviews(reviews) {
      if (!reviews.length) return;
      const html = reviews
        .map((r) => {
          const score = parseFloat(r.score ?? 0) || 0;
          const date = formatDate(reviewCreatedAt(r), this.locale);
          return `
            <article class="product-reviews-modal__review">
              <div class="product-reviews-modal__review-date">
                <span>${escapeHTML(date)}</span>
                <hr aria-hidden="true">
              </div>
              <div class="product-reviews-modal__review-header">
                <h3 class="product-reviews-modal__review-title" tabindex="-1">${escapeHTML(r.title || '')}</h3>
                <div class="product-reviews-modal__review-score">
                  ${dotsHTML(score, 'small')}
                  <span class="product-reviews-modal__review-score-num">${escapeHTML(score.toFixed(1))}</span>
                </div>
              </div>
              <p class="product-reviews-modal__review-content">${escapeHTML(r.content || '')}</p>
              <p class="product-reviews-modal__review-name">${escapeHTML(reviewerName(r, this.labels.anonymous))}</p>
            </article>
          `;
        })
        .join('');
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      this.refs.list.appendChild(tpl.content);
      this.shown += reviews.length;
    }

    updateCounter() {
      if (!this.refs.counter || !this.canonicalTotal) return;
      const text = this.labels.counter
        .replace('{shown}', this.shown.toLocaleString(this.locale))
        .replace('{total}', this.canonicalTotal.toLocaleString(this.locale));
      this.refs.counter.textContent = text;
      this.refs.counter.hidden = false;
    }

    showEndState() {
      if (!this.refs.endState) return;
      this.refs.endState.textContent = this.labels.endState;
      this.refs.endState.hidden = false;
    }

    setStatus(text) {
      if (!this.refs.status) return;
      if (text) {
        this.refs.status.textContent = text;
        this.refs.status.hidden = false;
      } else {
        this.refs.status.textContent = '';
        this.refs.status.hidden = true;
      }
    }
  }

  customElements.define('product-reviews-modal', ProductReviewsModal);
})();
